import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, CheckCircle2, Clock, ImageIcon, Loader2, Palette, Plug2, Save, TestTube2, Upload, XCircle, RefreshCw, Lock, Zap } from 'lucide-react'
import { toast } from 'sonner'

import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/app/components/ui/select'
import { Button } from '@/app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import { Input } from '@/app/components/ui/input'
import { http } from '@/app/lib/http/client'
import { settingsRecord, settingsService, settingValue, type ImageSettingValue } from '@/app/services/settings.service'
import { facturamaService } from '@/app/services/facturama.service'

const COLOR_KEYS = [
    { key: 'theme.primary_color', label: 'Primario' },
    { key: 'theme.secondary_color', label: 'Secundario' },
    { key: 'theme.accent_color', label: 'Acento' },
] as const

const IMAGE_KEYS = [
    { key: 'logo_main', label: 'Logo principal' },
    { key: 'logo_email', label: 'Logo correos' },
    { key: 'favicon', label: 'Favicon' },
] as const

const TIMEZONES = [
    'America/Mexico_City',
    'America/Monterrey',
    'America/Cancun',
    'America/Chihuahua',
    'America/Hermosillo',
    'America/Tijuana',
]

type ImageKey = typeof IMAGE_KEYS[number]['key']

interface FormState {
    company_name: string
    timezone: string
    'theme.primary_color': string
    'theme.secondary_color': string
    'theme.accent_color': string
    currency_symbol: string
    currency_code: string
    ticket_footer_text: string
    deleted_products_retention_days: string
    'integrations.facturama.username': string
    'integrations.facturama.password': string
    'integrations.facturama.sandbox_mode': boolean
    'integrations.wbs.user': string
    'integrations.wbs.password': string
    'integrations.wbs.base_url': string
}

const DEFAULT_FORM: FormState = {
    company_name: '',
    timezone: 'America/Mexico_City',
    'theme.primary_color': '#3B7D3B',
    'theme.secondary_color': '#2563EB',
    'theme.accent_color': '#F59E0B',
    currency_symbol: '$',
    currency_code: 'MXN',
    ticket_footer_text: '¡Gracias por su compra!',
    deleted_products_retention_days: '30',
    'integrations.facturama.username': '',
    'integrations.facturama.password': '',
    'integrations.facturama.sandbox_mode': true,
    'integrations.wbs.user': '',
    'integrations.wbs.password': '',
    'integrations.wbs.base_url': 'http://ws_stage.cloud-services.mx:9192/Service.asmx',
}

export function PlatformSettings() {
    const queryClient = useQueryClient()
    const [form, setForm] = useState<FormState>(DEFAULT_FORM)
    const [savedForm, setSavedForm] = useState<FormState>(DEFAULT_FORM)
    const [uploadingKey, setUploadingKey] = useState<ImageKey | null>(null)

    const { data: settings = [], isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: settingsService.getSettings,
    })

    const settingMap = useMemo(() => settingsRecord(settings), [settings])

    useEffect(() => {
        if (!settings.length) {
            return
        }

        const nextForm = {
            company_name: settingValue(settings, 'company_name', '') || '',
            timezone: settingValue(settings, 'timezone', 'America/Mexico_City'),
            'theme.primary_color': settingValue(settings, 'theme.primary_color', '#3B7D3B'),
            'theme.secondary_color': settingValue(settings, 'theme.secondary_color', '#2563EB'),
            'theme.accent_color': settingValue(settings, 'theme.accent_color', '#F59E0B'),
            currency_symbol: settingValue(settings, 'currency_symbol', '$'),
            currency_code: settingValue(settings, 'currency_code', 'MXN'),
            ticket_footer_text: settingValue(settings, 'ticket_footer_text', '¡Gracias por su compra!'),
            low_stock_threshold: String(settingValue(settings, 'low_stock_threshold', 5)),
            deleted_products_retention_days: String(settingValue(settings, 'deleted_products_retention_days', 30)),
            'integrations.facturama.username': settingValue(settings, 'integrations.facturama.username', ''),
            'integrations.facturama.password': '',
            'integrations.facturama.sandbox_mode': settingValue(settings, 'integrations.facturama.sandbox_mode', true),
            'integrations.facturama.expedition_place': settingValue(settings, 'integrations.facturama.expedition_place', '64257'),
            'integrations.facturama.cfdi_use': settingValue(settings, 'integrations.facturama.cfdi_use', 'G03'),
            'integrations.wbs.user': settingValue(settings, 'integrations.wbs.user', ''),
            'integrations.wbs.password': '',
            'integrations.wbs.base_url': settingValue(settings, 'integrations.wbs.base_url', 'http://ws_stage.cloud-services.mx:9192/Service.asmx'),
        }

        setForm(nextForm)
        setSavedForm(nextForm)
    }, [settings])

    const { data: facturamaProfile, isLoading: isLoadingProfile } = useQuery({
        queryKey: ['facturama-profile'],
        queryFn: facturamaService.getProfile,
        retry: false,
    })

    const updateMutation = useMutation({
        mutationFn: settingsService.updateSettings,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Configuracion actualizada')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo guardar la configuracion')
        },
    })

    const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const testConnectionMutation = useMutation({
        mutationFn: () => http.post('/integrations/facturama/test-connection', {
            username: form['integrations.facturama.username'],
            password: form['integrations.facturama.password'],
            sandbox_mode: form['integrations.facturama.sandbox_mode'],
        }),
        onSuccess: (res: any) => {
            setConnectionStatus('success')
            toast.success('Conexión con Facturama exitosa')
            if (res.data?.data) {
                queryClient.setQueryData(['facturama-profile'], res.data.data)
            }
        },
        onError: (error: any) => {
            setConnectionStatus('error')
            toast.error(error?.response?.data?.message ?? 'No se pudo conectar con Facturama')
        },
    })

    const [wbsConnectionStatus, setWbsConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const wbsTestConnectionMutation = useMutation({
        mutationFn: () => http.post('/integrations/wbs/test-connection', {
            user: form['integrations.wbs.user'],
            password: form['integrations.wbs.password'],
            base_url: form['integrations.wbs.base_url'],
        }),
        onSuccess: () => {
            setWbsConnectionStatus('success')
            toast.success('Conexión con WBS exitosa')
        },
        onError: (error: any) => {
            setWbsConnectionStatus('error')
            toast.error(error?.response?.data?.message ?? 'No se pudo conectar con WBS')
        },
    })

    const syncSatMutation = useMutation({
        mutationFn: () => {
            const limit = window.prompt('¿Cuántos registros deseas sincronizar? (Deja en blanco para todos)')
            const payload = limit ? { limit: Number(limit) } : {}
            return http.post('/integrations/facturama/sync-sat-keys', payload)
        },
        onSuccess: (response: any) => {
            toast.success(response.data?.message || 'Sincronización iniciada/completada con éxito')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo sincronizar claves SAT')
        },
    })

    const uploadMutation = useMutation({
        mutationFn: ({ key, file }: { key: ImageKey; file: File }) => settingsService.uploadAsset(key, file),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Imagen actualizada')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo subir la imagen')
        },
        onSettled: () => setUploadingKey(null),
    })

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const nextForm = {
            ...form,
            company_name: form.company_name.trim(),
        }
        const payload = changedSettingsPayload(nextForm, savedForm)

        if (Object.keys(payload).length === 0) {
            toast.info('No hay cambios por guardar')
            return
        }

        try {
            await updateMutation.mutateAsync(payload)
            setForm(nextForm)
            setSavedForm(nextForm)
        } catch {
            // El toast se emite en onError.
        }
    }

    const handleImageChange = async (key: ImageKey, file?: File) => {
        if (!file) {
            return
        }

        setUploadingKey(key)

        try {
            await uploadMutation.mutateAsync({ key, file })
        } catch {
            // El toast se emite en onError.
        }
    }

    return (
        <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold text-slate-900">Configuracion de plataforma</h1>
                    <p className="text-sm text-slate-500">
                        Ajusta la identidad visual que usa el punto de venta y las pantallas de acceso.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex min-h-64 items-center justify-center rounded-lg border border-slate-200 bg-white">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Identidad
                                    </CardTitle>
                                    <CardDescription>Nombre visible y recursos principales del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="space-y-2">
                                        <label htmlFor="company_name" className="text-sm font-medium text-slate-700">
                                            Nombre comercial
                                        </label>
                                        <Input
                                            id="company_name"
                                            value={form.company_name}
                                            onChange={(event) => setForm((current) => ({
                                                ...current,
                                                company_name: event.target.value,
                                            }))}
                                            placeholder="Farmacias Salud Plus"
                                        />
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                        {IMAGE_KEYS.map(({ key, label }) => {
                                            const image = settingMap[key]?.value as ImageSettingValue | null | undefined
                                            const isUploading = uploadingKey === key

                                            return (
                                                <div key={key} className="rounded-lg border border-slate-200 bg-white p-4">
                                                    <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50">
                                                        {image?.url ? (
                                                            <img src={image.url} alt={label} className="max-h-20 max-w-full object-contain" />
                                                        ) : (
                                                            <ImageIcon className="h-8 w-8 text-slate-300" />
                                                        )}
                                                    </div>
                                                    <div className="mt-3 space-y-2">
                                                        <p className="text-sm font-medium text-slate-800">{label}</p>
                                                        <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                                                            {isUploading ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <Upload className="h-4 w-4" />
                                                            )}
                                                            Subir
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpeg,image/webp"
                                                                className="sr-only"
                                                                disabled={isUploading}
                                                                onClick={(event) => {
                                                                    event.currentTarget.value = ''
                                                                }}
                                                                onChange={(event) => handleImageChange(key, event.target.files?.[0])}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Palette className="h-5 w-5 text-primary" />
                                        Paleta de colores
                                    </CardTitle>
                                    <CardDescription>Estos colores se aplican al tema activo del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="grid gap-4 md:grid-cols-3">
                                    {COLOR_KEYS.map(({ key, label }) => (
                                        <ColorField
                                            key={key}
                                            label={label}
                                            value={form[key]}
                                            onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                                        />
                                    ))}
                                </CardContent>
                            </Card>
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="text-base">Vista previa</CardTitle>
                                    <CardDescription>Referencia rapida del tema configurado.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-12 w-12 rounded-md"
                                                style={{ backgroundColor: form['theme.primary_color'] }}
                                            />
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">
                                                    {form.company_name || 'Farmacias Salud Plus'}
                                                </p>
                                                <p className="text-xs text-slate-500">{form.timezone}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-3 gap-2">
                                            {COLOR_KEYS.map(({ key, label }) => (
                                                <div key={key} className="space-y-1">
                                                    <div className="h-8 rounded-md" style={{ backgroundColor: form[key] }} />
                                                    <p className="text-xs text-slate-500">{label}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Plug2 className="h-5 w-5 text-primary" />
                                        Integraciones
                                    </CardTitle>
                                    <CardDescription>Conecta servicios externos con la plataforma.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="rounded-lg border border-slate-200 p-4 space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50">
                                                <span className="text-xs font-bold text-blue-600">F</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">Facturama</p>
                                                <p className="text-xs text-slate-500">Facturación electrónica CFDI 4.0</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="space-y-1.5">
                                                <label htmlFor="facturama_username" className="text-xs font-medium text-slate-700">
                                                    Usuario
                                                </label>
                                                <Input
                                                    id="facturama_username"
                                                    value={form['integrations.facturama.username']}
                                                    onChange={(e) => setForm((c) => ({ ...c, 'integrations.facturama.username': e.target.value }))}
                                                    placeholder="usuario_facturama"
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="facturama_password" className="text-xs font-medium text-slate-700">
                                                    Contraseña
                                                </label>
                                                <Input
                                                    id="facturama_password"
                                                    type="password"
                                                    value={form['integrations.facturama.password']}
                                                    onChange={(e) => { setConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.facturama.password': e.target.value })) }}
                                                    placeholder="••••••••"
                                                    autoComplete="new-password"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
                                                <div>
                                                    <p className="text-xs font-medium text-slate-700">Modo sandbox</p>
                                                    <p className="text-xs text-slate-400">Usar entorno de pruebas de Facturama</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={form['integrations.facturama.sandbox_mode']}
                                                    onClick={() => { setConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.facturama.sandbox_mode': !c['integrations.facturama.sandbox_mode'] })) }}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${form['integrations.facturama.sandbox_mode'] ? 'bg-primary' : 'bg-slate-200'
                                                        }`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${form['integrations.facturama.sandbox_mode'] ? 'translate-x-4' : 'translate-x-0.5'
                                                            }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                disabled={testConnectionMutation.isPending}
                                                onClick={() => testConnectionMutation.mutate()}
                                                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                {testConnectionMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <TestTube2 className="h-3.5 w-3.5" />
                                                )}
                                                Probar conexión
                                            </button>
                                            {connectionStatus === 'success' && (
                                                <span className="flex items-center gap-1 text-xs text-emerald-600">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
                                                </span>
                                            )}
                                            {connectionStatus === 'error' && (
                                                <span className="flex items-center gap-1 text-xs text-red-500">
                                                    <XCircle className="h-3.5 w-3.5" /> Sin conexión
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                                            <div>
                                                <p className="text-xs font-medium text-slate-700">Sincronización de Claves SAT</p>
                                                <p className="text-xs text-slate-500">Actualiza las descripciones de claves SAT genéricas con el catálogo real de Facturama.</p>
                                            </div>
                                            <button
                                                type="button"
                                                disabled={syncSatMutation.isPending}
                                                onClick={() => syncSatMutation.mutate()}
                                                className="inline-flex max-w-fit items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                {syncSatMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="h-3.5 w-3.5" />
                                                )}
                                                Sincronizar claves
                                            </button>
                                        </div>

                                        <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                                            <div>
                                                <p className="text-xs font-medium text-slate-700">Datos Fiscales - Facturacion</p>
                                                <p className="text-xs text-slate-500">Agrega los datos fiscales de la farmacia para generar las facturas de las ventas.</p>
                                            </div>
                                            <div className="space-y-3 bg-slate-50 p-4 rounded-lg border border-slate-100">
                                                {isLoadingProfile ? (
                                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        Cargando datos fiscales desde Facturama...
                                                    </div>
                                                ) : facturamaProfile ? (
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <p className="font-medium text-slate-500 text-xs">RFC Emisor</p>
                                                            <p className="font-semibold text-slate-900">{facturamaProfile.Rfc}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-500 text-xs">Razón Social</p>
                                                            <p className="font-semibold text-slate-900">{facturamaProfile.TaxName}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-500 text-xs">Régimen Fiscal</p>
                                                            <p className="font-semibold text-slate-900">{facturamaProfile.FiscalRegime}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-500 text-xs">Código Postal (Expedición)</p>
                                                            <p className="font-semibold text-slate-900">
                                                                {facturamaProfile.IssuedIn?.ZipCode || facturamaProfile.TaxAddress?.ZipCode || 'No configurado'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-amber-600">
                                                        No se pudieron cargar los datos fiscales. Verifica tus credenciales.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 p-4 space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-50">
                                                <span className="text-xs font-bold text-orange-600">W</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">WBS Recarga-me</p>
                                                <p className="text-xs text-slate-500">Recargas de tiempo aire y servicios</p>

                                            </div>

                                        </div>

                                        <div className="space-y-3">
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_user" className="text-xs font-medium text-slate-700">
                                                    Usuario
                                                </label>
                                                <Input
                                                    id="wbs_user"
                                                    value={form['integrations.wbs.user']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.user': e.target.value })) }}
                                                    placeholder="6144135400"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_password" className="text-xs font-medium text-slate-700">
                                                    Contraseña
                                                </label>
                                                <Input
                                                    id="wbs_password"
                                                    type="password"
                                                    value={form['integrations.wbs.password']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.password': e.target.value })) }}
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_base_url" className="text-xs font-medium text-slate-700">
                                                    URL del Servicio
                                                </label>
                                                <Input
                                                    id="wbs_base_url"
                                                    value={form['integrations.wbs.base_url']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.base_url': e.target.value })) }}
                                                    placeholder="http://ws_stage.cloud-services.mx:9192/Service.asmx"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                disabled={wbsTestConnectionMutation.isPending}
                                                onClick={() => wbsTestConnectionMutation.mutate()}
                                                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                {wbsTestConnectionMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <TestTube2 className="h-3.5 w-3.5" />
                                                )}
                                                Probar conexión
                                            </button>
                                            {wbsConnectionStatus === 'success' && (
                                                <span className="flex items-center gap-1 text-xs text-emerald-600">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
                                                </span>
                                            )}
                                            {wbsConnectionStatus === 'error' && (
                                                <span className="flex items-center gap-1 text-xs text-red-500">
                                                    <XCircle className="h-3.5 w-3.5" /> Sin conexión
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>


                        </div>

                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Clock className="h-5 w-5 text-primary" />
                                        Regional
                                    </CardTitle>
                                    <CardDescription>Configuraciones base para la compania.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <label htmlFor="timezone" className="text-sm font-medium text-slate-700">
                                        Zona horaria
                                    </label>
                                    <select
                                        id="timezone"
                                        value={form.timezone}
                                        onChange={(event) => setForm((current) => ({
                                            ...current,
                                            timezone: event.target.value,
                                        }))}
                                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                        {TIMEZONES.map((timezone) => (
                                            <option key={timezone} value={timezone}>
                                                {timezone}
                                            </option>
                                        ))}
                                    </select>
                                </CardContent>
                            </Card>

                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Punto de Venta
                                    </CardTitle>
                                    <CardDescription>Configuraciones de moneda, tickets e inventario.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="currency_symbol" className="text-sm font-medium text-slate-700">
                                                Símbolo de moneda
                                            </label>
                                            <Input
                                                id="currency_symbol"
                                                value={form.currency_symbol}
                                                onChange={(e) => setForm((c) => ({ ...c, currency_symbol: e.target.value }))}
                                                placeholder="$"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="currency_code" className="text-sm font-medium text-slate-700">
                                                Código de moneda
                                            </label>
                                            <Input
                                                id="currency_code"
                                                value={form.currency_code}
                                                onChange={(e) => setForm((c) => ({ ...c, currency_code: e.target.value }))}
                                                placeholder="MXN"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="ticket_footer_text" className="text-sm font-medium text-slate-700">
                                            Texto al final del ticket
                                        </label>
                                        <Input
                                            id="ticket_footer_text"
                                            value={form.ticket_footer_text}
                                            onChange={(e) => setForm((c) => ({ ...c, ticket_footer_text: e.target.value }))}
                                            placeholder="¡Gracias por su compra!"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="deleted_products_retention_days" className="text-sm font-medium text-slate-700">
                                                Días retención papelera
                                            </label>
                                            <Input
                                                id="deleted_products_retention_days"
                                                type="number"
                                                value={form.deleted_products_retention_days}
                                                onChange={(e) => setForm((c) => ({ ...c, deleted_products_retention_days: e.target.value }))}
                                                placeholder="30"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>


                            <PosPinSettings />


                            <div className="fixed bottom-4 right-4 flex justify-end">
                                <Button type="submit" size="lg" disabled={updateMutation.isPending}>
                                    {updateMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4" />
                                    )}
                                    Guardar cambios
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

function changedSettingsPayload(form: FormState, savedForm: FormState): Record<string, string | boolean | null> {
    const payload: Record<string, string | boolean | null> = {}
    const keys = Object.keys(form) as Array<keyof FormState>

    for (const key of keys) {
        if (form[key] === savedForm[key]) {
            continue
        }

        payload[key] = key === 'company_name' && form[key] === '' ? null : form[key]
    }

    return payload
}

function ColorField({
    label,
    value,
    onChange,
}: {
    label: string
    value: string
    onChange: (value: string) => void
}) {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">{label}</label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={(event) => onChange(event.target.value.toUpperCase())}
                    className="h-10 w-12 rounded-md border border-slate-200 bg-white p-1"
                />
                <Input
                    value={value}
                    onChange={(event) => onChange(event.target.value.toUpperCase())}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="font-mono text-sm"
                />
            </div>
        </div>
    )
}

export function PosPinSettings() {
    const [password, setPassword] = useState('')
    const [pin, setPin] = useState('')
    const [pinConfirmation, setPinConfirmation] = useState('')

    const mutation = useMutation({
        mutationFn: settingsService.updatePosPin,
        onSuccess: () => {
            toast.success('PIN de caja actualizado exitosamente')
            setPassword('')
            setPin('')
            setPinConfirmation('')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo actualizar el PIN')
        }
    })

    const handleSubmit = () => {
        if (!password || !pin || !pinConfirmation) {
            toast.error('Todos los campos son obligatorios')
            return
        }
        if (pin !== pinConfirmation) {
            toast.error('El PIN y su confirmación no coinciden')
            return
        }
        if (pin.length < 4) {
            toast.error('El PIN debe tener al menos 4 dígitos')
            return
        }
        mutation.mutate({ password, pin, pin_confirmation: pinConfirmation })
    }

    return (
        <Card className="rounded-lg">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Lock className="h-5 w-5 text-primary" />
                    Seguridad de Caja
                </CardTitle>
                <CardDescription>
                    Configura el PIN global para asignar o cambiar de caja. (Solo administradores).
                </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Contraseña actual</label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Tu contraseña actual"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Nuevo PIN</label>
                    <Input
                        type="password"
                        maxLength={8}
                        pattern="\d+"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej. 1234"
                        className="font-mono tracking-widest"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Confirmar PIN</label>
                    <Input
                        type="password"
                        maxLength={8}
                        pattern="\d+"
                        value={pinConfirmation}
                        onChange={(e) => setPinConfirmation(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej. 1234"
                        className="font-mono tracking-widest"
                    />
                </div>
            </CardContent>
            <div className="flex justify-end p-6 pt-0">
                <Button
                    type="button"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={mutation.isPending}
                >
                    {mutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="mr-2 h-4 w-4" />
                    )}
                    Actualizar PIN
                </Button>
            </div>
        </Card>
    )
}
